import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  FilePdf,
  CurrencyEur,
  Check,
  Clock,
  X,
  Truck,
  Car
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePermissions } from "@/hooks/usePermissions";

const STATUS_LABELS = {
  brouillon: { label: "Brouillon", color: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" },
  validee: { label: "Validee", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  payee: { label: "Payee", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  partiellement_payee: { label: "Partiel", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  annulee: { label: "Annulee", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" }
};

const fmt = (v) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0);

const PurchaseInvoices = () => {
  const { can } = usePermissions();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form
  const [fournisseurId, setFournisseurId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [montantHt, setMontantHt] = useState("");
  const [tvaApplicable, setTvaApplicable] = useState(true);
  const [tvaTaux, setTvaTaux] = useState(21);
  const [modePaiement, setModePaiement] = useState("virement");
  const [notes, setNotes] = useState("");
  const [lignes, setLignes] = useState([{ description: "", quantite: 1, prix_unitaire: 0, montant: 0 }]);

  // Reference data
  const [fournisseurs, setFournisseurs] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  // Payment form
  const [payMontant, setPayMontant] = useState("");
  const [payMode, setPayMode] = useState("virement");
  const [payRef, setPayRef] = useState("");

  const canCreate = can("purchase_invoices", "create");
  const canEdit = can("purchase_invoices", "update");
  const canDelete = can("purchase_invoices", "delete");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/purchase-invoices?page=${page}&per_page=20`);
      setInvoices(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page]);

  const fetchRefs = useCallback(async () => {
    try {
      const [fRes, vRes] = await Promise.all([
        axios.get(`${API}/fournisseurs?per_page=200`),
        axios.get(`${API}/vehicles?per_page=200`)
      ]);
      setFournisseurs(fRes.data.data || []);
      setVehicles(vRes.data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchRefs(); }, [fetchData, fetchRefs]);

  const resetForm = () => {
    setFournisseurId(""); setVehicleId(""); setMontantHt(""); setTvaApplicable(true);
    setTvaTaux(21); setModePaiement("virement"); setNotes("");
    setLignes([{ description: "", quantite: 1, prix_unitaire: 0, montant: 0 }]);
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const updateLine = (idx, field, val) => {
    setLignes(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      if (field === "prix_unitaire" || field === "quantite") {
        next[idx].montant = (next[idx].prix_unitaire || 0) * (next[idx].quantite || 1);
      }
      return next;
    });
  };

  const addLine = () => setLignes(prev => [...prev, { description: "", quantite: 1, prix_unitaire: 0, montant: 0 }]);
  const removeLine = (idx) => setLignes(prev => prev.filter((_, i) => i !== idx));

  // Auto-calculate from lines
  const totalLines = lignes.reduce((s, l) => s + (l.montant || 0), 0);
  const effectiveHt = montantHt ? parseFloat(montantHt) : totalLines;
  const effectiveTva = tvaApplicable ? effectiveHt * tvaTaux / 100 : 0;
  const effectiveTtc = effectiveHt + effectiveTva;

  // Auto-detect TVA based on fournisseur type
  useEffect(() => {
    if (fournisseurId) {
      const f = fournisseurs.find(f => f.fournisseur_id === fournisseurId);
      if (f && f.type_fournisseur === "particulier") {
        setTvaApplicable(false);
      }
    }
  }, [fournisseurId, fournisseurs]);

  const handleSave = async () => {
    if (!effectiveHt || effectiveHt <= 0) { toast.error("Montant HT requis"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/purchase-invoices`, {
        fournisseur_id: fournisseurId || undefined,
        vehicle_id: vehicleId || undefined,
        montant_ht: effectiveHt,
        tva_applicable: tvaApplicable,
        tva_taux: tvaTaux,
        mode_paiement: modePaiement,
        notes: notes || undefined,
        lignes: lignes.filter(l => l.description)
      });
      toast.success("Facture d'achat creee");
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/purchase-invoices/${id}`);
      toast.success("Facture supprimee");
      fetchData();
    } catch { toast.error("Erreur suppression"); }
  };

  const handlePay = async () => {
    if (!selectedInv || !payMontant) return;
    try {
      await axios.post(`${API}/purchase-invoices/${selectedInv.purchase_invoice_id}/payment`, {
        montant: parseFloat(payMontant),
        mode_paiement: payMode,
        reference: payRef || undefined
      });
      toast.success("Paiement enregistre");
      setPayDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openPay = (inv) => {
    setSelectedInv(inv);
    setPayMontant(String(inv.montant_ttc - (inv.montant_paye || 0)));
    setPayMode("virement");
    setPayRef("");
    setPayDialogOpen(true);
  };

  const downloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/purchase-invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `facture_achat_${id}.pdf`; a.click();
    } catch { toast.error("Erreur PDF"); }
  };

  const filtered = search
    ? invoices.filter(i =>
        (i.numero || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.fournisseur_nom || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.vehicle_description || "").toLowerCase().includes(search.toLowerCase())
      )
    : invoices;

  return (
    <div className="space-y-6" data-testid="purchase-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-slate-900 dark:text-slate-100">Factures d'achat</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Enregistrement des achats vehicules</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="new-purchase-invoice-btn">
            <Plus size={16} className="mr-1.5" /> Nouvelle facture achat
          </Button>
        )}
      </div>

      <div className="card p-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par numero, fournisseur, vehicule..." className="pl-9" data-testid="purchase-invoice-search" />
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center"><div className="spinner h-8 w-8 mx-auto"></div></div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <Receipt size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500">Aucune facture d'achat</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(inv => {
            const st = STATUS_LABELS[inv.statut] || STATUS_LABELS.brouillon;
            return (
              <div key={inv.purchase_invoice_id} className="card p-4 hover:shadow-md transition-all" data-testid={`purchase-invoice-${inv.purchase_invoice_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                      <Receipt size={20} className="text-orange-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{inv.numero}</span>
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 font-medium">ACHAT</span>
                        <span className={`px-2 py-0.5 text-[10px] rounded-full ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                        {inv.fournisseur_nom && <span className="flex items-center gap-1"><Truck size={12} /> {inv.fournisseur_nom}</span>}
                        {inv.vehicle_description && <span className="flex items-center gap-1"><Car size={12} /> {inv.vehicle_description}</span>}
                        <span>{inv.date_facture ? new Date(inv.date_facture).toLocaleDateString("fr-BE") : ""}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0 ml-3">
                    <div className="text-right">
                      <p className="font-bold text-sm text-slate-900 dark:text-slate-100">{fmt(inv.montant_ttc)}</p>
                      {!inv.tva_applicable && <p className="text-[10px] text-slate-400">Sans TVA</p>}
                      {inv.montant_paye > 0 && inv.montant_paye < inv.montant_ttc && (
                        <p className="text-[10px] text-amber-600">Paye: {fmt(inv.montant_paye)}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => downloadPdf(inv.purchase_invoice_id)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`pdf-${inv.purchase_invoice_id}`}><FilePdf size={16} className="text-red-500" /></button>
                      {canEdit && inv.statut !== "payee" && (
                        <button onClick={() => openPay(inv)} className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20" data-testid={`pay-${inv.purchase_invoice_id}`}><CurrencyEur size={16} className="text-green-600" /></button>
                      )}
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`delete-${inv.purchase_invoice_id}`}><Trash size={14} className="text-red-400" /></button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Supprimer cette facture ?</AlertDialogTitle><AlertDialogDescription>Cette action est irreversible.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(inv.purchase_invoice_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Precedent</Button>
          <span className="text-sm text-slate-500">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Suivant</Button>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle facture d'achat</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Fournisseur</Label>
                <Select value={fournisseurId || "none"} onValueChange={v => setFournisseurId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="pi-fournisseur"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {fournisseurs.map(f => (
                      <SelectItem key={f.fournisseur_id} value={f.fournisseur_id}>
                        {f.entreprise || f.nom} ({f.type_fournisseur === "professionnel" ? "Pro" : "Part."})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Vehicule</Label>
                <Select value={vehicleId || "none"} onValueChange={v => setVehicleId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="pi-vehicle"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {vehicles.map(v => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.marque} {v.modele} - {v.reference_interne}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lignes */}
            <div>
              <Label className="form-label">Lignes de facture</Label>
              <div className="space-y-2">
                {lignes.map((l, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input className="flex-1" placeholder="Description" value={l.description} onChange={e => updateLine(idx, "description", e.target.value)} data-testid={`pi-line-desc-${idx}`} />
                    <Input className="w-16" type="number" placeholder="Qte" value={l.quantite} onChange={e => updateLine(idx, "quantite", parseInt(e.target.value) || 1)} />
                    <Input className="w-28" type="number" placeholder="Prix unit." value={l.prix_unitaire || ""} onChange={e => updateLine(idx, "prix_unitaire", parseFloat(e.target.value) || 0)} data-testid={`pi-line-price-${idx}`} />
                    <span className="text-sm font-medium w-24 text-right">{fmt(l.montant)}</span>
                    {lignes.length > 1 && <button onClick={() => removeLine(idx)} className="p-1 text-red-400"><X size={14} /></button>}
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addLine}>+ Ajouter ligne</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Montant HT</Label>
                <Input type="number" value={montantHt || totalLines || ""} onChange={e => setMontantHt(e.target.value)} placeholder="Auto-calcule depuis lignes" data-testid="pi-montant-ht" />
              </div>
              <div>
                <Label className="form-label">TVA applicable</Label>
                <Select value={tvaApplicable ? "oui" : "non"} onValueChange={v => setTvaApplicable(v === "oui")}>
                  <SelectTrigger data-testid="pi-tva-applicable"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oui">Oui ({tvaTaux}%)</SelectItem>
                    <SelectItem value="non">Non (particulier)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Totals preview */}
            <div className="bg-orange-50 dark:bg-orange-900/10 rounded-xl p-4 border border-orange-200 dark:border-orange-800/30">
              <div className="flex justify-between text-sm"><span>Montant HT</span><span className="font-medium">{fmt(effectiveHt)}</span></div>
              <div className="flex justify-between text-sm mt-1"><span>TVA ({tvaApplicable ? `${tvaTaux}%` : "N/A"})</span><span className="font-medium">{tvaApplicable ? fmt(effectiveTva) : "---"}</span></div>
              <div className="flex justify-between text-base font-bold mt-2 pt-2 border-t border-orange-200 dark:border-orange-700"><span>Total TTC</span><span className="text-orange-700 dark:text-orange-400">{fmt(effectiveTtc)}</span></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Mode de paiement</Label>
                <Select value={modePaiement} onValueChange={setModePaiement}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="especes">Especes</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="carte">Carte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div><Label className="form-label">Notes</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="pi-notes" /></div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="pi-save-btn">
                {saving ? "..." : "Creer la facture"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={(v) => !v && setPayDialogOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
          {selectedInv && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Facture {selectedInv.numero} — Restant: <strong>{fmt(selectedInv.montant_ttc - (selectedInv.montant_paye || 0))}</strong>
              </p>
              <div><Label className="form-label">Montant</Label><Input type="number" value={payMontant} onChange={e => setPayMontant(e.target.value)} data-testid="pay-montant" /></div>
              <div>
                <Label className="form-label">Mode</Label>
                <Select value={payMode} onValueChange={setPayMode}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="especes">Especes</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="form-label">Reference</Label><Input value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="Ref. bancaire" /></div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setPayDialogOpen(false)}>Annuler</Button>
                <Button onClick={handlePay} className="bg-green-600 hover:bg-green-700 text-white" data-testid="pay-confirm-btn">Confirmer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseInvoices;
